import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, Clock3, RefreshCw, Search, ShieldCheck } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_LABELS = {
  PENDENTE: 'Aguardando resposta',
  AUTORIZADO: 'Autorizado',
  RECUSADO: 'Recusado'
};

const STATUS_STYLES = {
  PENDENTE: 'border-amber-300 bg-amber-50 text-amber-800',
  AUTORIZADO: 'border-emerald-300 bg-emerald-50 text-emerald-800',
  RECUSADO: 'border-rose-300 bg-rose-50 text-rose-800'
};

const formatDate = (value) => value
  ? new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value))
  : '-';

const formatPhone = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length === 13 && digits.startsWith('55')) {
    return `+55 (${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9)}`;
  }
  return digits || '-';
};

const readTokenFromFragment = () => {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  return params.get('token') || '';
};

export default function CompanyConsentPortal() {
  const [token] = useState(readTokenFromFragment);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('RECUSADO');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [selectedConsent, setSelectedConsent] = useState(null);
  const [reopening, setReopening] = useState(false);

  useEffect(() => {
    if (token) {
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    }
  }, [token]);

  const request = useCallback(async (path, options = {}) => {
    const response = await fetch(`${API_URL}${path}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(options.headers || {})
      }
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(body.error || 'Não foi possível concluir a operação.');
    }
    return body;
  }, [token]);

  const loadConsents = useCallback(async () => {
    if (!token) {
      setLoading(false);
      setError('Abra este painel pelo MasterVendas para iniciar uma sessão segura.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page: '1', limit: '50' });
      if (status !== 'TODOS') {
        params.set('status', status);
      }

      const normalizedSearch = search.replace(/\D/g, '');
      if (normalizedSearch) {
        params.set('search', normalizedSearch);
      }

      const response = await request(`/api/company-portal/consents?${params.toString()}`);
      setData(response.data);
    } catch (requestError) {
      setData(null);
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [request, search, status, token]);

  useEffect(() => {
    const timer = window.setTimeout(loadConsents, 250);
    return () => window.clearTimeout(timer);
  }, [loadConsents]);

  const selectedNumber = useMemo(
    () => formatPhone(selectedConsent?.numero_normalizado),
    [selectedConsent]
  );

  const reopenConsent = async () => {
    if (!selectedConsent) {
      return;
    }

    setReopening(true);
    setError('');
    try {
      const response = await request(`/api/company-portal/consents/${selectedConsent.id}/reopen`, {
        method: 'POST'
      });
      setNotice(response.message);
      setSelectedConsent(null);
      await loadConsents();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setReopening(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900" data-testid="company-consent-portal">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-5 sm:px-6">
          <ShieldCheck className="h-7 w-7 text-emerald-700" aria-hidden="true" />
          <div>
            <h1 className="text-xl font-semibold">Autorizações do WhatsApp</h1>
            <p className="text-sm text-slate-600">
              {data?.empresa?.nome_empresa || 'Gerenciamento protegido da empresa'}
            </p>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        <section className="border border-slate-200 bg-white p-4 sm:p-5" aria-label="Contatos e autorizações">
          <div className="flex flex-col gap-4 border-b border-slate-200 pb-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="text-base font-semibold">Contatos</h2>
              <p className="mt-1 max-w-2xl text-sm text-slate-600">
                Uma nova solicitação nunca envia o pedido automaticamente. O próximo envio solicita a autorização novamente ao contato.
              </p>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={loadConsents}
              disabled={loading}
              title="Atualizar lista"
              aria-label="Atualizar lista"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_220px]">
            <div className="space-y-1.5">
              <Label htmlFor="consent-search">Número</Label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" aria-hidden="true" />
                <Input
                  id="consent-search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="DDD e número"
                  className="pl-9"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="consent-status">Situação</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger id="consent-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="RECUSADO">Recusados</SelectItem>
                  <SelectItem value="PENDENTE">Aguardando resposta</SelectItem>
                  <SelectItem value="AUTORIZADO">Autorizados</SelectItem>
                  <SelectItem value="TODOS">Todos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {notice && (
            <p className="mt-4 border-l-4 border-emerald-500 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
              {notice}
            </p>
          )}
          {error && (
            <p className="mt-4 flex items-start gap-2 border-l-4 border-rose-500 bg-rose-50 px-3 py-2 text-sm text-rose-900">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              {error}
            </p>
          )}

          <div className="mt-4 overflow-x-auto border border-slate-200">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-4 py-3 font-medium">Número</th>
                  <th className="px-4 py-3 font-medium">Situação</th>
                  <th className="px-4 py-3 font-medium">Última alteração</th>
                  <th className="px-4 py-3 font-medium">Ação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {loading && (
                  <tr><td colSpan="4" className="px-4 py-8 text-center text-slate-500">Carregando contatos...</td></tr>
                )}
                {!loading && data?.consents?.length === 0 && (
                  <tr><td colSpan="4" className="px-4 py-8 text-center text-slate-500">Nenhum contato encontrado.</td></tr>
                )}
                {!loading && data?.consents?.map((consent) => (
                  <tr key={consent.id}>
                    <td className="px-4 py-3 font-medium text-slate-800">{formatPhone(consent.numero_normalizado)}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={STATUS_STYLES[consent.status]}>
                        {STATUS_LABELS[consent.status]}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{formatDate(consent.updated_at)}</td>
                    <td className="px-4 py-3">
                      {consent.status === 'RECUSADO' && consent.can_reopen && (
                        <Button size="sm" variant="outline" onClick={() => setSelectedConsent(consent)}>
                          Nova solicitação
                        </Button>
                      )}
                      {consent.status === 'RECUSADO' && !consent.can_reopen && (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500" title={consent.reopen_reason}>
                          <Clock3 className="h-3.5 w-3.5" aria-hidden="true" />
                          Disponível em {formatDate(consent.reopen_available_at)}
                        </span>
                      )}
                      {consent.status !== 'RECUSADO' && (
                        <span className="text-xs text-slate-500">Sem ação necessária</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      <AlertDialog
        open={Boolean(selectedConsent)}
        onOpenChange={(open) => !open && !reopening && setSelectedConsent(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permitir nova solicitação?</AlertDialogTitle>
            <AlertDialogDescription>
              O número {selectedNumber} receberá somente um novo pedido de autorização no próximo envio. Nenhum pedido, cobrança ou anexo será enviado agora.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reopening}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={reopenConsent} disabled={reopening}>
              {reopening ? 'Preparando...' : 'Permitir nova solicitação'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
